/**
 * Color mappings for literary forms in the outline view
 * Uses CSS color values for maximum compatibility
 */

// Form-specific colors (most specific) - using hex values
// Color scheme organized by genre with distinct color families for better differentiation
const FORM_COLORS: Record<string, string> = {
  // Narrative forms - Blue/Cyan family (storytelling)
  'Parable': '#0ea5e9',              // sky-500 - bright blue
  'Miracle Story': '#06b6d4',        // cyan-500 - cyan
  'Genealogy': '#64748b',            // slate-500 - muted blue-gray
  'Epic': '#1e40af',                 // blue-700 - deep blue
  'Theological History': '#3b82f6',  // blue-500 - medium blue
  'Prophet Story': '#0284c7',        // sky-600 - darker sky blue
  'Announcement': '#38bdf8',         // sky-400 - light sky blue
  'Epiphany Report': '#0891b2',      // cyan-600 - darker cyan
  'Battle Report': '#0c4a6e',        // sky-900 - very dark blue
  'Historical Story': '#2563eb',     // blue-600 - darker blue
  'Anecdote': '#22d3ee',             // cyan-400 - light cyan
  'Memoir': '#075985',               // sky-700 - deep sky blue
  'Speech': '#0369a1',               // sky-700 - darker sky
  'Instruction': '#0e7490',          // cyan-700 - deep cyan
  'Commission': '#164e63',           // cyan-900 - very dark cyan

  // Poetry forms - Purple/Violet family (artistic/emotional)
  'Individual Lament': '#7c3aed',    // violet-600 - medium violet
  'Communal Lament': '#6d28d9',      // violet-700 - darker violet
  'Thanksgiving Song': '#a855f7',    // purple-500 - medium purple
  'Hymn': '#9333ea',                 // purple-600 - darker purple
  'Royal Psalm': '#c026d3',          // fuchsia-600 - bright purple-pink
  'Wisdom Psalm': '#a21caf',         // fuchsia-700 - darker fuchsia
  'Song of Zion': '#d946ef',         // fuchsia-500 - bright fuchsia
  'Confidence Psalm': '#c084fc',     // purple-400 - light purple
  'Imprecatory Psalm': '#7e22ce',    // purple-700 - deep purple
  'Torah Psalm': '#581c87',          // purple-900 - very dark purple

  // Prophecy forms - Red/Orange family (warning/proclamation)
  'Prophecy': '#dc2626',             // red-600 - medium red
  'Woe Oracle': '#ea580c',           // orange-600 - dark orange
  'Salvation Oracle': '#f97316',     // orange-500 - bright orange
  'Vision Report': '#ef4444',        // red-500 - bright red
  'Lawsuit': '#b91c1c',              // red-700 - dark red
  'Disputation': '#c2410c',          // orange-700 - darker orange

  // Wisdom forms - Yellow/Amber family (enlightenment/teaching)
  'Proverb': '#eab308',              // yellow-500 - bright yellow
  'Monologue': '#f59e0b',            // amber-500 - amber
  'Dialogue': '#d97706',             // amber-600 - darker amber

  // Law forms - Gray/Slate family (structure/order)
  'Legal Series': '#475569',         // slate-600 - medium slate
  'Legal Instruction': '#64748b',    // slate-500 - lighter slate
  'Casuistic Law': '#334155',        // slate-700 - darker slate
  'Apodictic Law': '#1e293b',        // slate-800 - very dark slate

  // Epistle forms - Green/Teal family (teaching/community)
  'Creeds and Hymns': '#059669',     // emerald-600 - medium emerald
  'Household Codes': '#10b981',      // emerald-500 - bright emerald
  'Vice and Virtue Lists': '#14b8a6', // teal-500 - teal
  'Doxology': '#0d9488',             // teal-600 - darker teal
  'Benediction': '#0f766e',          // teal-700 - deep teal
  'Paranesis': '#047857',            // emerald-700 - dark emerald
};

// Genre fallback colors (when form is not in the map or empty)
const GENRE_COLORS: Record<string, string> = {
  'Narrative': '#3b82f6',   // blue-500 - blue for storytelling
  'Poetry': '#a855f7',      // purple-500 - purple for artistic/emotional
  'Wisdom': '#eab308',      // yellow-500 - yellow for enlightenment
  'Prophecy': '#dc2626',    // red-600 - red for warning/proclamation
  'Epistle': '#10b981',     // emerald-500 - green for teaching/community
  'Law': '#64748b',         // slate-500 - gray for structure/order
};

/**
 * Get the color value for a passage based on its form and genre
 * @param form - The literary form (most specific)
 * @param genre - The genre (fallback)
 * @returns CSS color value (hex)
 */
export function getFormBorderColor(form: string | undefined, genre: string | undefined): string {
  // Try form first (most specific)
  if (form && FORM_COLORS[form]) {
    return FORM_COLORS[form];
  }

  // Fall back to genre
  if (genre && GENRE_COLORS[genre]) {
    return GENRE_COLORS[genre];
  }

  // Default fallback - neutral gray
  return '#9ca3af'; // gray-400
}

/**
 * Get a readable label for the color (for tooltips/legends)
 */
export function getFormColorLabel(form: string | undefined, genre: string | undefined): string {
  if (form) {
    return form;
  }
  if (genre) {
    return genre;
  }
  return 'Unknown';
}

/**
 * Get all form colors for the legend
 * Returns a map of form/genre names to their border colors
 */
export function getAllFormColors(): Map<string, string> {
  return new Map(Object.entries(FORM_COLORS));
}

/**
 * Get all genre colors for the legend
 * Returns a map of genre names to their border colors
 */
export function getAllGenreColors(): Map<string, string> {
  return new Map(Object.entries(GENRE_COLORS));
}

